import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  Box,
  Stack,
  Slide,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { motion, animate, stagger } from "motion/react";
import { FaCheckCircle } from "react-icons/fa";
import Header from "../components/header";
import Footer from "../components/footer";
import getApiBase from "../components/getApiBase";
import TurnstileWidget from "../components/TurnstileWidget";
import {
  PUBLIC_INFO_EMAIL,
  publicAiPhoneDisplay,
  publicAiPhoneTelHref,
  publicInfoMailtoHref,
} from "../constants/publicContact";

import contactImage from "../assets/newContactImage.png";

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    success: true,
    message: "",
  });
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClose = () => {
    setFeedback({ ...feedback, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (turnstileSiteKey && !turnstileToken) {
      setFeedback({
        open: true,
        success: false,
        message: "Please complete the bot verification challenge.",
      });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${getApiBase()}/api/contact/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });

      const result = await res.json();
      if (res.ok) {
        setFeedback({
          open: true,
          success: true,
          message: "Message sent successfully!",
        });
        setFormData({ name: "", email: "", subject: "", message: "" });
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setShowSuccessIcon(true);
        setTimeout(() => setShowSuccessIcon(false), 3000);
      } else {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setFeedback({
          open: true,
          success: false,
          message: result.error || "Failed to send message.",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      setFeedback({
        open: true,
        success: false,
        message: "An error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const subject = searchParams.get("subject") || "";
    const name = searchParams.get("name") || "";
    const email = searchParams.get("email") || "";
    if (!subject && !name && !email) return;
    setFormData((prev) => ({
      ...prev,
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
      ...(subject ? { subject } : {}),
    }));
  }, [searchParams]);

  // ANIMATIONS
    useEffect(() => {
      const userInputs = Array.from(document.getElementsByClassName("contact-item"));
      if (userInputs.length > 0) {
        animate(
          userInputs,
          { x: [-30, 0], opacity: [0, 1] },
          { delay: stagger(0.3) }
        );
      }
    }, []);

  return (
    <Box>
      <Header />
      <motion.div
        className="contact__image"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ ease: "easeOut", duration: 2 }}
      >
        <div className="img__shader" />
        <Container
          maxWidth="lg"
          sx={{
            py: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
            }}
          >
            {/* Contact Form */}
            <motion.div
              initial={{ y: "-100%", }}
              animate={{ y: 0, zIndex: 10 }}
              transition={{ ease: "easeOut", duration: 1, }}
            >
            <Paper
              elevation={5}
              sx={{
                p: 4,
                borderRadius: "20px",
                minWidth: { xs: "100%", md: "500px" },
                zIndex: 10,
              }}
            >
              <Typography
              className="contact-item"
                variant="h4"
                fontWeight={600}
                gutterBottom
                align="center"
              >
                Contact Us
              </Typography>
              <Typography
              className="contact-item" variant="body1" sx={{ mb: 2 }} align="center">
                We would love to hear from you! Please fill out the form below.
              </Typography>

              <Box
                className="contact-item"
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Email:{" "}
                  <Box
                    component="a"
                    href={publicInfoMailtoHref()}
                    sx={{ color: "primary.main", fontWeight: 600 }}
                  >
                    {PUBLIC_INFO_EMAIL}
                  </Box>
                </Typography>
                <Typography variant="body2">
                  AI Phone Assistant:{" "}
                  <Box
                    component="a"
                    href={`tel:${publicAiPhoneTelHref()}`}
                    sx={{ color: "primary.main", fontWeight: 600 }}
                  >
                    {publicAiPhoneDisplay()}
                  </Box>
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                  className="contact-item"
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                  <TextField
                  className="contact-item"
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    type="email"
                    required
                    error={
                      formData.email && !/\S+@\S+\.\S+/.test(formData.email)
                    }
                    helperText={
                      formData.email && !/\S+@\S+\.\S+/.test(formData.email)
                        ? "Enter a valid email"
                        : ""
                    }
                  />

                  <TextField
                  className="contact-item"
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    fullWidth
                  />
                  <TextField
                  className="contact-item"
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    rows={5}
                    fullWidth
                    required
                  />
                  <Box className="contact-item">
                    <TurnstileWidget
                      ref={turnstileRef}
                      onVerify={setTurnstileToken}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                    />
                  </Box>
                  <Button
                  className="contact-item"
                  type="submit"
                  variant="contained"
                  disabled={loading || (turnstileSiteKey ? !turnstileToken : false)}>
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </Stack>
              </form>

              {showSuccessIcon && (
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <FaCheckCircle size={50} color="green" />
                </Box>
              )}
            </Paper>
            </motion.div>
          </Box>
        </Container>
      </motion.div>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={feedback.success ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
      <Footer />
    </Box>
  );
};

export default Contact;
